import React, { Suspense, lazy } from 'react'
import { Container, Input, Checkbox, Divider, Loader } from 'components/UIElements'
import '../stylesheets/createStudy.scss'
import PropTypes from 'prop-types'
import { browserHistory } from 'utils/navigation'
import { STUDY_CONFIG_MAP, MIN_SESSION_TIMEOUT_MINUTES } from 'utils/constants'
import STRINGS from 'utils/strings'
import { pluralize } from 'utils/misc'
import { confettiIcon } from 'assets/assets'
import { generatePClassName, CREATE_STUDY_FULL_URLS } from '../utils/studyUtils'
import BinaryConfig from './BinaryConfig'
import EnforcedLanguagesConfig from './EnforcedLanguagesConfig'
import SecurityConfig from './SecurityConfig'
import CreateStudyFooter from './CreateStudyFooter'
import ParticipantOfflineDuration from './ParticipantOfflineDuration'

const IDFormatBuilder = lazy(() => import(/* webpackChunkName: "dnd-kit-idformat" */ './IDFormatBuilder'))
import {
  parseFormatString,
  generateFormatString,
  findFieldByKey,
  updateField,
  ensureFieldExists,
  relocateFieldByRequired,
} from './idFormatUtils'
import { PRESCREEN_ID_KEY, PRESCREEN_ID_LABEL, SUBJECT_ID_KEY, SUBJECT_ID_LABEL } from '../utils/constants'

const richButtonIconMap = {
  ban: <i className='fas fa-ban' />,
  squareCheck: <i className='fas fa-check-square' />,
  dataSync: (
    <div className='data-sync-icon flexed column'>
      <div />
    </div>
  ),
  confetti: confettiIcon,
  iconContainer: icon => {
    return (
      <div className='mid-term-reward-icon flexed column end-justified'>
        <div>{icon()}</div>
      </div>
    )
  },
}

class CreateStudySetting extends React.Component {
  state = {
    prescreenOpen: true,
    subjectOpen: true,
  }
  updateStudyConfig = newConfig => {
    const { study, updateConfig } = this.props
    const config = study.config || {}
    const _config = config || {}
    updateConfig({
      ..._config,
      ...{ ...newConfig },
    })
  }

  deleteConfigKey = key => {
    const { study, updateConfig } = this.props
    const config = study.config || {}
    if (!config) return
    const newConfig = config
    delete newConfig[key]
    updateConfig(newConfig)
  }

  getHoursAndMins(mins) {
    const hours = Math.floor(mins / 60)
    const leftMins = mins % 60
    return [hours, leftMins]
  }

  renderTimeoutInput = sessionTimeoutConfig => {
    const { disabled, study } = this.props
    const configObj = study.config || study
    const { admin_security, session_timeout_seconds } = configObj
    const { updateValue, updateStudySecurityConfig, key, currentSettings } = sessionTimeoutConfig || {}
    const onChange = sessionTimeoutConfig
      ? updateValue(updateStudySecurityConfig, key, currentSettings)
      : val => this.updateStudyConfig({ session_timeout_seconds: val * 60 })
    const adminSessionTimeout =
      admin_security?.[key] ?? admin_security?.session_timeout ?? admin_security?.timeout_flag
    const sessionValue = sessionTimeoutConfig
      ? disabled && (adminSessionTimeout === undefined || adminSessionTimeout === null || adminSessionTimeout === '')
        ? '30'
        : adminSessionTimeout || ''
      : session_timeout_seconds
      ? session_timeout_seconds / 60
      : ''
    const [hours, mins] = this.getHoursAndMins(sessionValue)
    const exampleTime =
      hours > 0 ? ` ( ${pluralize(hours, 'hour', 'hours')} ${mins > 0 ? pluralize(mins, 'min ', 'mins ') : ''}) ` : ' '
    return (
      <Input
        className='user-session-timeout'
        disabled={disabled}
        inline
        onChange={onChange}
        onBlur={event => {
          if (Number(event.target.value) < MIN_SESSION_TIMEOUT_MINUTES) {
            if (sessionTimeoutConfig) {
              updateValue(updateStudySecurityConfig, key, currentSettings)
              this.updateStudyConfig({
                admin_security: {
                  ...admin_security,
                  [key]: MIN_SESSION_TIMEOUT_MINUTES,
                },
              })
            } else {
              this.updateStudyConfig({ session_timeout_seconds: MIN_SESSION_TIMEOUT_MINUTES * 60 })
            }
          }
        }}
        pretext='Timeout after'
        posttext={`${pluralize(sessionValue, 'minute', 'minutes', false)}${exampleTime}`}
        preventInput={num => num < 0 || num > 999}
        max={999}
        id='session-timeout'
        type='number'
        value={sessionValue}
      />
    )
  }

  render() {
    const { disabled, isRootOrDchAdmin, saveAttempted, study } = this.props
    const configObj = study.config || study
    const { hasEnforcedLanguage, enforced_language } = configObj
    const configProps = {
      ...this.props,
      configObj,
      deleteConfigKey: this.deleteConfigKey,
      updateConfig: this.updateStudyConfig,
    }
    const hasLanguageError =
      saveAttempted && hasEnforcedLanguage && !!enforced_language && enforced_language.default_language === ''

    const removeParticipantField = internalKey => {
      const pdf = configObj.participant_data_fields || { mandatory: [], optional: [] }
      const filterOut = (arr = []) => arr.filter(f => f?.metadata?.internal_key !== internalKey)
      const nextPdf = {
        mandatory: filterOut(pdf.mandatory),
        optional: filterOut(pdf.optional),
        disabled: (pdf.disabled || []).filter(f => f?.metadata?.internal_key !== internalKey),
      }
      this.updateStudyConfig({ participant_data_fields: nextPdf })
    }

    const handleConfigToggle = ({ keyString, coupledKeyString, val = {}, onEnable, onDisable }) => () => {
      const enabled = !!configObj[keyString]
      if (enabled) {
        this.deleteConfigKey(keyString)
        if (coupledKeyString) this.deleteConfigKey(coupledKeyString)
        if (onDisable) onDisable()
        return
      }
      const update = { [keyString]: val }
      if (coupledKeyString) update[coupledKeyString] = val
      this.updateStudyConfig(update)
      if (onEnable) onEnable()
    }

    const ensureRequiredOnEnable = (internalKey, displayLabel) => {
      let newFields = ensureFieldExists(configObj.participant_data_fields, internalKey, displayLabel, true)
      newFields = relocateFieldByRequired(newFields, internalKey, true)
      this.updateStudyConfig({ participant_data_fields: newFields })
    }

    const configCheckbox = ({
      id,
      label,
      coupledKeyString,
      keyString,
      toggle = false,
      val = {},
      onEnable,
      onDisable,
    }) => {
      return (
        <Checkbox
          id={id}
          toggle={toggle}
          label={label}
          checked={!!configObj[keyString]}
          disabled={disabled}
          onClick={handleConfigToggle({ keyString, coupledKeyString, val, onEnable, onDisable })}
        />
      )
    }

    // Get Pre-Screen ID field configuration
    const prescreenField = findFieldByKey(configObj.participant_data_fields, PRESCREEN_ID_KEY)
    const prescreenIsAutoGen = prescreenField?.metadata?.is_auto_generated || false
    // Required is derived from array placement (mandatory vs optional), not metadata
    // Default to Required if field doesn't exist yet
    const prescreenIsRequired = prescreenField
      ? (configObj.participant_data_fields?.mandatory || []).some(f => f?.metadata?.internal_key === PRESCREEN_ID_KEY)
      : true
    const prescreenFormatString = prescreenField?.metadata?.auto_gen_format || ''
    const prescreenPattern = parseFormatString(prescreenFormatString)

    const handlePatternChange = (internalKey, displayLabel, isRequired) => pattern => {
      const formatString = generateFormatString(pattern)
      let newFields = ensureFieldExists(configObj.participant_data_fields, internalKey, displayLabel, isRequired)
      newFields = updateField(newFields, internalKey, { auto_gen_format: formatString })
      this.updateStudyConfig({ participant_data_fields: newFields })
    }

    const handleModeChange = (internalKey, displayLabel, currentIsRequired) => changes => {
      let newFields = ensureFieldExists(
        configObj.participant_data_fields,
        internalKey,
        displayLabel,
        changes.isRequired !== undefined ? changes.isRequired : currentIsRequired,
      )

      const updates = {}
      if (changes.isAutoGenerate !== undefined) {
        updates.is_auto_generated = changes.isAutoGenerate
      }

      newFields = updateField(newFields, internalKey, updates)
      if (changes.isRequired !== undefined) {
        newFields = relocateFieldByRequired(newFields, internalKey, changes.isRequired)
      }

      this.updateStudyConfig({
        participant_data_fields: newFields,
      })
    }

    const prescreenIdConfig = (
      <div className='id-configuration-section'>
        <div
          className={`section-toggle-bar ${this.state.prescreenOpen ? 'open' : ''}`}
          onClick={() => this.setState({ prescreenOpen: !this.state.prescreenOpen })}
          role='button'
          tabIndex={0}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') this.setState({ prescreenOpen: !this.state.prescreenOpen })
          }}>
          <span className='title'>{PRESCREEN_ID_LABEL}</span>
          <span className='chevron' aria-hidden='true' />
        </div>
        {this.state.prescreenOpen && (
          <Suspense fallback={<div className='flexed center-justified' style={{ minHeight: 80 }}><Loader inContainer size={24} /></div>}>
            <IDFormatBuilder
              idType='prescreen'
              isAutoGenerate={prescreenIsAutoGen}
              isRequired={prescreenIsRequired}
              pattern={prescreenPattern}
              onChange={handlePatternChange(PRESCREEN_ID_KEY, PRESCREEN_ID_LABEL, prescreenIsRequired)}
              onModeChange={handleModeChange(PRESCREEN_ID_KEY, PRESCREEN_ID_LABEL, prescreenIsRequired)}
              disabled={disabled}
            />
          </Suspense>
        )}
      </div>
    )

    // Get Subject ID field configuration
    const subjectField = findFieldByKey(configObj.participant_data_fields, SUBJECT_ID_KEY)
    const subjectIsAutoGen = subjectField?.metadata?.is_auto_generated || false
    // Required is derived from array placement (mandatory vs optional), not metadata
    // Default to Required if field doesn't exist yet
    const subjectIsRequired = subjectField
      ? (configObj.participant_data_fields?.mandatory || []).some(f => f?.metadata?.internal_key === SUBJECT_ID_KEY)
      : true
    const subjectFormatString = subjectField?.metadata?.auto_gen_format || ''
    const subjectPattern = parseFormatString(subjectFormatString)

    // Validate that incremental digits exist when auto-generate is on
    const hasIncrToken = formatString => (formatString || '').includes('{INCR:')
    const prescreenValid = !prescreenIsAutoGen || hasIncrToken(prescreenFormatString)
    const subjectValid = !subjectIsAutoGen || hasIncrToken(subjectFormatString)
    const footerDisabled = !(prescreenValid && subjectValid)

    // Ensure unchecked ID types are stripped before proceeding
    const sanitizeAndGoNext = () => {
      const pdf = configObj.participant_data_fields || { mandatory: [], optional: [], disabled: [] }
      const enabledPrescreen = !!configObj.pre_screen_id_enabled
      const enabledSubject = !!configObj.subject_id_enabled

      const shouldKeep = f => {
        const key = f?.metadata?.internal_key
        if (key === PRESCREEN_ID_KEY) return enabledPrescreen
        if (key === SUBJECT_ID_KEY) return enabledSubject
        return true
      }

      const cleanedPdf = {
        mandatory: (pdf.mandatory || []).filter(shouldKeep),
        optional: (pdf.optional || []).filter(shouldKeep),
        disabled: (pdf.disabled || []).filter(shouldKeep),
      }

      this.updateStudyConfig({ participant_data_fields: cleanedPdf })
      browserHistory.push(CREATE_STUDY_FULL_URLS.schedule)
    }

    const subjectIdConfig = (
      <div className='id-configuration-section'>
        <div
          className={`section-toggle-bar ${this.state.subjectOpen ? 'open' : ''}`}
          onClick={() => this.setState({ subjectOpen: !this.state.subjectOpen })}
          role='button'
          tabIndex={0}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') this.setState({ subjectOpen: !this.state.subjectOpen })
          }}>
          <span className='title'>Subject ID</span>
          <span className='chevron' aria-hidden='true' />
        </div>
        {this.state.subjectOpen && (
          <Suspense fallback={<div className='flexed center-justified' style={{ minHeight: 80 }}><Loader inContainer size={24} /></div>}>
            <IDFormatBuilder
              idType='subject'
              isAutoGenerate={subjectIsAutoGen}
              isRequired={subjectIsRequired}
              pattern={subjectPattern}
              onChange={handlePatternChange(SUBJECT_ID_KEY, SUBJECT_ID_LABEL, subjectIsRequired)}
            onModeChange={handleModeChange(SUBJECT_ID_KEY, SUBJECT_ID_LABEL, subjectIsRequired)}
              disabled={disabled}
            />
          </Suspense>
        )}
      </div>
    )

    const endOfStudyConfig = (
      <div>
        <h5>{STRINGS.endOfStudySummary}</h5>
        <p className={generatePClassName(configObj, STUDY_CONFIG_MAP.studyEnd, disabled)}>
          {STRINGS.endOfStudyConfigBody}
        </p>
        {configCheckbox({
          id: 'end-of-summary',
          label: STRINGS.displayEndOfStudy,
          keyString: STUDY_CONFIG_MAP.studyEnd,
        })}
      </div>
    )

    const ParticipantSection = (
      <Container>
        <h3>{STRINGS.ptpAppSettings}</h3>
        {isRootOrDchAdmin && (
          <>
            <h5>{STRINGS.languages}</h5>
            <EnforcedLanguagesConfig
              {...this.props}
              resetEnforcedLanguage={() => this.updateStudyConfig({ hasEnforcedLanguage: false })}
              updateConfig={this.updateStudyConfig}
              hasError={hasLanguageError}
            />
            <Divider />

            {/* ID Selection Section */}
            <div className='id-selection-section'>
              <h5>{STRINGS.selectIdsHeading}</h5>
              <div className='id-checkboxes'>
                {configCheckbox({
                  id: 'pre-screen-id',
                  label: PRESCREEN_ID_LABEL,
                  keyString: 'pre_screen_id_enabled',
                  toggle: false,
                  val: true,
                  onEnable: () => ensureRequiredOnEnable(PRESCREEN_ID_KEY, PRESCREEN_ID_LABEL),
                  onDisable: () => removeParticipantField(PRESCREEN_ID_KEY),
                })}
                {configCheckbox({
                  id: 'subject-id',
                  label: SUBJECT_ID_LABEL,
                  keyString: 'subject_id_enabled',
                  toggle: false,
                  val: true,
                  onEnable: () => ensureRequiredOnEnable(SUBJECT_ID_KEY, SUBJECT_ID_LABEL),
                  onDisable: () => removeParticipantField(SUBJECT_ID_KEY),
                })}
              </div>
            </div>

            {/* ID Configuration Sections */}
            {configObj.pre_screen_id_enabled && (
              <>
                <Divider />
                {prescreenIdConfig}
              </>
            )}
            {configObj.subject_id_enabled && (
              <>
                <Divider />
                {subjectIdConfig}
              </>
            )}

            <Divider />
            {endOfStudyConfig}
            <Divider />
          </>
        )}
        <h5>Session Time Out</h5>
        {this.renderTimeoutInput()}
      </Container>
    )

    const SecuritySection = (
      <Container>
        <h3>{STRINGS.security}</h3>
        <SecurityConfig
          {...configProps}
          updateStudySecurityConfig={newConfig => this.updateStudyConfig({ admin_security: { ...newConfig } })}
          renderTimeoutInput={this.renderTimeoutInput}
        />
      </Container>
    )

    const ToneSection = (
      <Container>
        <BinaryConfig
          {...this.props}
          bodyText={STRINGS.toneOfVoiceBody}
          buttonIcons={{ leftIcon: richButtonIconMap.squareCheck, rightIcon: richButtonIconMap.confetti }}
          buttonLabels={{ left: STRINGS.neutral, right: STRINGS.celebratory }}
          configKey={STUDY_CONFIG_MAP.toneOfVoice}
          defaultIsFalse
          heading={STRINGS.toneOfVoice}
          updateStudyConfig={this.updateStudyConfig}
        />
      </Container>
    )

    const DataSyncSection = (
      <Container>
        <BinaryConfig
          {...this.props}
          bodyText={STRINGS.dataSyncConfigBody}
          buttonIcons={{ leftIcon: richButtonIconMap.ban, rightIcon: richButtonIconMap.dataSync }}
          buttonLabels={{ left: STRINGS.hide, right: STRINGS.show }}
          configKey={STUDY_CONFIG_MAP.dataSyncUI}
          heading={STRINGS.dataSync}
          updateStudyConfig={this.updateStudyConfig}
        />
        <ParticipantOfflineDuration study={study} disabled={disabled} updateStudyConfig={this.updateStudyConfig} />
      </Container>
    )

    return (
      <>
        {ParticipantSection}
        {ToneSection}
        {DataSyncSection}
        {SecuritySection}
        <CreateStudyFooter
          {...this.props}
          disabled={this.props.disabled || false || footerDisabled}
          title={STRINGS.next}
          onClick={sanitizeAndGoNext}
        />
      </>
    )
  }
}

CreateStudySetting.propTypes = {
  createStudy: PropTypes.func,
  deleteStudy: PropTypes.func,
  disabled: PropTypes.bool,
  fetchLocales: PropTypes.func,
  hasDeleteStudyPermissions: PropTypes.bool,
  isRootOrDchAdmin: PropTypes.bool,
  isWizard: PropTypes.bool,
  saveAttempted: PropTypes.bool,
  setNavBarProps: PropTypes.func,
  study: PropTypes.shape({
    self_sign_code: PropTypes.string,
    name: PropTypes.string,
    platform_name: PropTypes.string,
    id: PropTypes.number,
  }),
  updateConfig: PropTypes.func,
  updateStudy: PropTypes.func,
}

export default CreateStudySetting
